import { get } from "svelte/store";
import { auth, googleAuthProvider } from "./firebase";
import { signInWithEmailAndPassword, signOut as firebaseSignOut, onAuthStateChanged, AuthErrorCodes, signInWithPopup, signInWithRedirect, } from "firebase/auth";
import { isFunction } from "./utils";
import axios, { setAxiosAuthToken, clearAxiosAuthToken } from "./axios";
import { currentUserStore, setCurrentUser, userInfoStore, initAuthProcess, } from "../store/store-user";
import { callApiWithAnonymousAuth, createHeader, axiosCall } from "./common";
import { updateUserInfo } from "./user";
import checkMobile from "../../components/common/is-mobile";
import { isKnownDomain, isLocalhost } from "../config";
import pageUrl from "../../PageUrl";
export async function signIn(email, password, config = {}) {
    try {
        let userCredential = await signInWithEmailAndPassword(auth, email, password);
        const token = await userCredential.user.getIdToken();
        setAxiosAuthToken(token);
        if (isFunction(config.onSuccess))
            await config.onSuccess();
    }
    catch (error) {
        let key = "";
        let message = "";
        switch (error.code) {
            case AuthErrorCodes.USER_DELETED: {
                key = "email";
                message = "User not found.";
                break;
            }
            case AuthErrorCodes.INVALID_PASSWORD: {
                key = "password";
                message = "Incorrect password.";
                break;
            }
            case AuthErrorCodes.NETWORK_REQUEST_FAILED: {
                message = "Server is unreachable. Please try again in a few minutes.";
                break;
            }
            default:
                message = "Something went wrong. Please try again in sometime.";
                break;
        }
        const apiError = { key, message };
        if (isFunction(config.onError))
            await config.onError(apiError);
    }
    ;
}
export async function signInWithGoogle() {
    try {
        if (!checkMobile()) {
            await signInWithPopup(auth, googleAuthProvider);
        }
        else {
            await signInWithRedirect(auth, googleAuthProvider);
        }
    }
    catch (error) {
        console.log("Error: ", error);
    }
}
export async function register(email, password, firstname, lastname, config = {}) {
    const requestConfig = {
        url: "/users",
        method: 'post',
        data: {
            email: email,
            password,
            firstName: firstname,
            lastName: lastname,
        }
    };
    return callApiWithAnonymousAuth(async () => {
        var _a;
        try {
            const res = await axios(requestConfig);
            if (isFunction(config.onSuccess))
                await config.onSuccess();
            return res;
        }
        catch (error) {
            let message = "";
            let key = "";
            if ((_a = error.response) === null || _a === void 0 ? void 0 : _a.data) {
                key = error.response.data.key;
                message = error.response.data.message;
            }
            else {
                message = "Something went wrong. Please try again in sometime.";
            }
            const apiError = { key, message };
            if (isFunction(config.onError))
                await config.onError(apiError);
            // console.log("Register Error: ", error);
        }
    }, config);
}
export function signOut() {
    return firebaseSignOut(auth).then(() => {
        // Sign-out successful.
        clearAxiosAuthToken();
        setUiPageLogInStatus(false);
        return true;
    }).catch((error) => {
        // An error happened.
        // console.log("Sign out error: ", error);
        return false;
    });
}
export function getCurrentUser() {
    return auth.currentUser;
}
export async function sendVerificationEmail(config = {}) {
    if (!get(userInfoStore)) {
        console.log("Dont have user email to send email");
        return;
    }
    const requestConfig = { url: '/auth/sendVerificationEmail', method: 'post', headers: createHeader({}) };
    try {
        const res = await axios(requestConfig);
        if (isFunction(config.onSuccess))
            await config.onSuccess(res.data);
        return res.data;
    }
    catch (error) {
        if (isFunction(config.onError))
            await config.onError(error);
    }
}
export async function checkVerifyEmailCode(code, config = {}) {
    if (!get(userInfoStore)) {
        console.log("Dont have user email to verify code");
        return;
    }
    const requestConfig = { url: '/auth/verifyEmail', method: 'post', data: { code } };
    try {
        const res = await axios(requestConfig);
        await forceRefreshUser();
        if (isFunction(config.onSuccess))
            await config.onSuccess(res.data);
        return res.data;
    }
    catch (error) {
        if (isFunction(config.onError))
            await config.onError(error);
    }
}
/**
 * Make sure the user information is up-to-date after changes.
 *
 * In case email has just been verified, need to refresh id token to make
 * sure it contains verified_email is true, and reload user for
 * client use as well.
 */
export async function forceRefreshUser() {
    const user = get(currentUserStore);
    await user.reload();
    await refreshIdToken();
}
/**
 * Get new Id token regardless of token expiration.
 */
export async function refreshIdToken() {
    const user = get(currentUserStore);
    let token = await user.getIdToken(true);
    setAxiosAuthToken(token);
}
export async function sendResetPasswordRequest(email, config = {}) {
    const requestConfig = { url: '/auth/resetPassword', method: 'post', data: { email } };
    try {
        const res = await axios(requestConfig);
        if (isFunction(config.onSuccess))
            await config.onSuccess(res.data);
        return res.data;
    }
    catch (error) {
        if (isFunction(config.onError))
            await config.onError(error);
    }
}
/**
 * Get custom auth token
 * @param config
 * @returns
 */
export async function getCustomAuthToken(config = {}) {
    const requestConfig = { url: '/auth/getCustomToken', method: 'post' };
    const data = await axiosCall(requestConfig, config);
    return data;
}
const actionSessionKey = "action";
const redirectUrlSessionKey = "redirectUrl";
/**
 * Set user id cookie
 *
 * It acts as a hint to let site on subdomain know that the status of
 * current logged in user. If there is not or user id not match, the subdomains
 * site know that the user is log out or user account has been changed.
 * @param user
 */
function setSubdomainUserIdCookie(user) {
    const uid = user ? user.uid : "";
    const hostname = isLocalhost ? location.hostname : "datadocs.com";
    document.cookie = `dduid=${uid};domain=${hostname};secure;samesite=strict`;
}
export function setUiPageLogInStatus(loggedIn) {
    const hostname = isLocalhost ? location.hostname : "datadocs.com";
    const status = loggedIn ? "true" : "false";
    document.cookie = `ddui-login-status=${status};path=/;domain=${hostname};secure;samesite=strict`;
}
/**
 * Check if it is request from subdomains to authentication
 * Put the action and redirect url to session storage to use
 * after user has been logged in successfully.
 * @returns
 */
export function storeAuthRedirectSession() {
    const location = window.location;
    // const pathName = location.pathname;
    // // only allow login page to have authentication redirect behavior
    // if (pathName.indexOf("/login") === -1) return;
    const params = new URLSearchParams(location.search);
    const action = params.get(actionSessionKey);
    const redirectUrl = params.get(redirectUrlSessionKey);
    if (action === "auth" && isKnownDomain(redirectUrl)) {
        sessionStorage.setItem(actionSessionKey, action);
        sessionStorage.setItem(redirectUrlSessionKey, redirectUrl);
    }
    // After login in, we need to redirect the page to the UI/main page
    if (action !== "auth" &&
        !sessionStorage.getItem(redirectUrlSessionKey)) {
        sessionStorage.setItem(actionSessionKey, "auth");
        sessionStorage.setItem(redirectUrlSessionKey, pageUrl.UI_PAGE);
    }
}
/**
 * Redirect user back to the subdomains that need to be login
 * It send back the custom auth token
 * @param info User information
 * @returns false if not going to redirect and true otherwise
 */
export async function handleAuthRedirectAfterLogin(info) {
    if (!info)
        return false;
    function removeSessionData() {
        sessionStorage.removeItem(actionSessionKey);
        sessionStorage.removeItem(redirectUrlSessionKey);
    }
    const action = sessionStorage.getItem(actionSessionKey);
    const redirectUrl = sessionStorage.getItem(redirectUrlSessionKey);
    let url;
    // Make sure user has plan before allow to login in subdomains
    // Also if the session storage data not match redirecting
    // condition, return without doing custom auth process
    if (!info.plan ||
        action !== "auth" ||
        !isKnownDomain(redirectUrl))
        return false;
    try {
        url = new URL(redirectUrl);
    }
    catch (error) {
        // redirect url is not valid, so do nothing
        removeSessionData();
        return false;
    }
    let success = false;
    // make sure the token issue time is still not expired
    // to be able to call getCustomToken
    await refreshIdToken();
    await getCustomAuthToken({
        onSuccess: async (data) => {
            const customToken = data.token;
            url.searchParams.append("customToken", customToken);
            url.searchParams.append("action", "auth");
            const newUrl = url.toString();
            removeSessionData();
            setSubdomainUserIdCookie(get(currentUserStore));
            window.location.replace(newUrl);
            success = true;
        },
        onError: async (error) => {
            removeSessionData();
            success = false;
        }
    });
    return success;
}
/**
 * Listen to Auth state changed, include firebase init loading
 * logged in user that's stored on IndexedDB
 */
export function listenToAuthStateChanged() {
    onAuthStateChanged(auth, async (user) => {
        if (user && !user.isAnonymous) {
            // User is signed in,
            setSubdomainUserIdCookie(user);
            const token = await user.getIdToken();
            setAxiosAuthToken(token);
            setCurrentUser(user);
            await updateUserInfo();
            // console.log("User information in auth event: ", user);
        }
        else {
            // User is signed out or there is anonymous account
            setSubdomainUserIdCookie(null);
            clearAxiosAuthToken();
            setCurrentUser(null);
            setUiPageLogInStatus(false);
        }
        initAuthProcess.set(true);
    });
}
